/**
 * Offline FX renderer (iOS playback path when EQ/compressor are enabled).
 *
 * Instead of routing the live <audio> element through a realtime AudioContext
 * (which iOS interrupts aggressively on CarPlay/Bluetooth routes and screen
 * lock — see HYBRID_AUDIO_ROUTING_PLAN.md), this service renders the FX chain
 * faster than realtime with an OfflineAudioContext and returns a WAV blob URL
 * that AudioService plays through the native media pipeline.
 *
 * OfflineAudioContext never touches the audio hardware or output route, so
 * none of the realtime interruption/sample-rate problems apply.
 */

import { buildFxChain } from './fxChain';
import { postProcessRender } from './fxPostProcess';

// decodeAudioData resamples to the decode context's rate; 44.1 kHz matches the library.
const DECODE_SAMPLE_RATE = 44100;

class OfflineFxService {
  constructor() {
    // Single-entry cache of the *compressed* track bytes (mp3/aac/wav as served).
    // Decoded PCM is intentionally not cached: ~100 MB for a 5-minute track is
    // too heavy for older devices, while compressed bytes are ~5-10 MB and
    // re-decode in ~1-2 s (acceptable behind the settings debounce).
    this._cachedUrl = null;
    this._cachedBytes = null; // ArrayBuffer
    this._fetchAbortController = null;

    // Any render whose generation no longer matches is discarded (never applied).
    this._generation = 0;
  }

  /**
   * Invalidate the byte cache and cancel in-flight work (e.g., on track change).
   */
  invalidate() {
    this.bumpGeneration();
    if (this._fetchAbortController) {
      this._fetchAbortController.abort();
      this._fetchAbortController = null;
    }
    this._cachedUrl = null;
    this._cachedBytes = null;
  }

  /**
   * Make any in-flight render stale so its result is discarded.
   */
  bumpGeneration() {
    this._generation++;
  }

  /**
   * Render the track at `url` through the FX chain described by `settings`.
   *
   * @param {string} url - Original track URL
   * @param {object} settings - Snapshot: { eqSettings, compressorSettings, eqEnabled, compressorEnabled, autoMakeupEnabled }
   * @returns {Promise<{blobUrl: string, reductionEnvelope: {rate:number, values:Float32Array}|null}|null>}
   *          null if superseded by a newer render/track change
   */
  async render(url, settings) {
    const generation = ++this._generation;

    const bytes = await this._getBytes(url);
    if (generation !== this._generation || !bytes) return null;

    // decodeAudioData detaches the ArrayBuffer — always hand it a copy so the
    // cache survives for the next re-render.
    const decoded = await this._decode(bytes.slice(0));
    if (generation !== this._generation || !decoded) return null;

    const OfflineCtx = window.OfflineAudioContext || window.webkitOfflineAudioContext;
    const renderCtx = new OfflineCtx(
      decoded.numberOfChannels,
      decoded.length,
      decoded.sampleRate
    );

    const source = renderCtx.createBufferSource();
    source.buffer = decoded;

    const chain = buildFxChain(renderCtx, settings);
    source.connect(chain.input);
    chain.output.connect(renderCtx.destination);
    source.start(0);

    const rendered = await renderCtx.startRendering();
    if (generation !== this._generation) return null;

    // WAV encode + (optionally) the GR meter envelope. Both are per-sample
    // scans of full-length buffers — done in a Web Worker when possible so
    // the main thread never stalls (see fxPostProcess.js). The envelope only
    // exists for the FX dialog's meter, so it is skipped when the caller
    // doesn't need it (settings.wantEnvelope === false, e.g. dialog closed).
    const wantEnvelope = settings.compressorEnabled && settings.wantEnvelope !== false;
    let wavBuffer;
    let reductionEnvelope = null;
    try {
      const processed = await postProcessRender(
        rendered,
        wantEnvelope ? decoded : null,
        settings.compressorSettings
      );
      wavBuffer = processed.wavBuffer;
      reductionEnvelope = processed.envelope;
    } catch (error) {
      console.error('OfflineFxService: post-processing failed', error);
      return null;
    }
    if (generation !== this._generation) return null;

    const blob = new Blob([wavBuffer], { type: 'audio/wav' });
    return { blobUrl: URL.createObjectURL(blob), reductionEnvelope };
  }

  /**
   * Build a clean (unprocessed) blob URL from cached bytes, for instant
   * network-free revert when the user turns all FX off.
   * @param {string} url - Original track URL
   * @returns {string|null} blob URL, or null if the bytes are not cached
   */
  getOriginalBlobUrl(url) {
    if (this._cachedUrl === url && this._cachedBytes) {
      return URL.createObjectURL(new Blob([this._cachedBytes]));
    }
    return null;
  }

  /**
   * Fetch (or reuse cached) compressed bytes for the URL.
   * @param {string} url
   * @returns {Promise<ArrayBuffer|null>}
   */
  async _getBytes(url) {
    if (this._cachedUrl === url && this._cachedBytes) {
      return this._cachedBytes;
    }

    if (this._fetchAbortController) {
      this._fetchAbortController.abort();
    }
    this._fetchAbortController = new AbortController();

    try {
      // Same CORS posture as AudioService.prefetchTrack — no backend changes needed.
      const response = await fetch(url, {
        mode: 'cors',
        signal: this._fetchAbortController.signal
      });
      if (!response.ok) throw new Error(`FX render fetch failed: ${response.status}`);
      const bytes = await response.arrayBuffer();

      this._cachedUrl = url;
      this._cachedBytes = bytes;
      this._fetchAbortController = null;
      return bytes;
    } catch (error) {
      this._fetchAbortController = null;
      if (error && error.name === 'AbortError') return null;
      console.error('OfflineFxService: failed to fetch track bytes', error);
      return null;
    }
  }

  /**
   * Decode compressed bytes to an AudioBuffer.
   * Uses a throwaway OfflineAudioContext (never the realtime context — iOS
   * must not see any realtime audio activity from this path).
   * Supports both promise and callback decodeAudioData signatures.
   *
   * @param {ArrayBuffer} bytes
   * @returns {Promise<AudioBuffer|null>}
   */
  _decode(bytes) {
    const OfflineCtx = window.OfflineAudioContext || window.webkitOfflineAudioContext;
    const decodeCtx = new OfflineCtx(2, 1, DECODE_SAMPLE_RATE);

    return new Promise((resolve) => {
      try {
        const result = decodeCtx.decodeAudioData(
          bytes,
          (buffer) => resolve(buffer),
          (error) => {
            console.error('OfflineFxService: decode failed', error);
            resolve(null);
          }
        );
        // Modern promise-based signature (resolve is idempotent, double-call is safe)
        if (result && typeof result.then === 'function') {
          result.then(
            (buffer) => resolve(buffer),
            (error) => {
              console.error('OfflineFxService: decode failed', error);
              resolve(null);
            }
          );
        }
      } catch (error) {
        console.error('OfflineFxService: decode threw', error);
        resolve(null);
      }
    });
  }
}

export default new OfflineFxService();
